import { useTranslation } from 'react-i18next'

import { useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import {
  mapPageGlobalMaxErrorSelector,
  mapPageGlobalMinErrorSelector,
  mapPageGlobalSpotMaxErrorSelector,
  mapPageGlobalSpotMinErrorSelector,
} from '@/store/cartSlice/selectors/mapPageErrorsSelector'
import { selectTotalGlobalSelectedQuantity } from '@/store/cartSlice/selectors/selectTotalGlobalSelectedQuantity.ts'
import { totalGlobalBoughtQuantitySelector } from '@/store/cartSlice/selectors/totalGlobalBoughtQuantitySelector'
import {
  mapSelectedElementHasVisibleSeatsSelector,
  mapSelectedElementServices,
  mapSelectors,
} from '@/store/mapSlice'
import { mapVisibleTicketGroupsSelector } from '@/store/mapSlice/selectors/mapVisibleTicketGroupsSelector'
import { servicesSlice } from '@/store/servicesSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import { getVisibleServices } from '@/utils/getVisibleServices'
import { sumServiceGroupSelectedQuantity } from '@/utils/sumServiceGroupSelectedQuantity'

import { SetupServiceListItem } from './SetupServiceListItem'
import {
  SetupTicketGlobalConstraints,
  SetupTicketGroups,
} from './SetupTicketGroups'

export const SetupServiceList = () => {
  const { t } = useTranslation()

  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const selectedElementQuotableProducts = useAppSelector(
    cartSlice.selectors.selectedElementQuotableProducts
  )
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const selectedElementHasVisibleSeats = useAppSelector(
    mapSelectedElementHasVisibleSeatsSelector
  )
  const selectedElementServices = useAppSelector(mapSelectedElementServices)
  const visibleTicketGroups = useAppSelector(mapVisibleTicketGroupsSelector)
  const servicesData = useAppSelector(servicesSlice.selectors.data)
  const submitted = useAppSelector(ticketSlice.selectors.submitted)
  const validationScope = useAppSelector(ticketSlice.selectors.validationScope)
  const showReservationErrors = submitted && validationScope === 'reservation'
  const showSpotErrors = submitted && validationScope === 'spot'
  const globalMinError = useAppSelector(mapPageGlobalMinErrorSelector)
  const globalMaxError = useAppSelector(mapPageGlobalMaxErrorSelector)
  const globalSpotMinError = useAppSelector(mapPageGlobalSpotMinErrorSelector)
  const globalSpotMaxError = useAppSelector(mapPageGlobalSpotMaxErrorSelector)

  const includedServices = selectedElementServices.includedServices
  const extraServices = selectedElementServices.extraServices
  const ticketServices = selectedElementServices.ticketServices

  if (!selectedElement) return null

  const visibleIncludedServices = getVisibleServices({
    selectedElementHasVisibleSeats,
    services: includedServices,
  })
  const visibleExtraServices = getVisibleServices({
    selectedElementHasVisibleSeats,
    services: extraServices,
  })

  const totalGlobalSelectedQuantity = useAppSelector(
    selectTotalGlobalSelectedQuantity
  )
  const totalGlobalBoughtQuantity = useAppSelector(
    totalGlobalBoughtQuantitySelector
  )

  const getOverideValues = (service: Service) => {
    if (service.scheduleType !== 'daily') {
      return {}
    }

    const totalGroupBoughtQuantity = getGroupBoughtQuantity({
      groupId: service.serviceGroupId,
      selectedElementQuotableProducts,
      ticketServices,
    })

    const totalGroupSelectedQuantity = sumServiceGroupSelectedQuantity({
      cartReservations,
      selectedElement,
      selectedElementQuotableProducts,
      service,
      ticketServices,
    })

    const globalAvailableQnt =
      (service.globalAvailableQnt ?? 0) - totalGlobalBoughtQuantity
    const globalReservedQnt =
      (service.globalReservedQnt ?? 0) + totalGlobalSelectedQuantity

    const groupAvailableQnt =
      (service.groupAvailableQnt ?? 0) - totalGroupBoughtQuantity
    const groupReservedQnt =
      (service.groupReservedQnt ?? 0) + totalGroupSelectedQuantity

    return {
      globalAvailableQnt,
      globalReservedQnt,
      groupAvailableQnt,
      groupReservedQnt,
    }
  }

  const hasVisibleTicketGroups = visibleTicketGroups.length > 0

  return (
    <div className="flex flex-col">
      {visibleIncludedServices.length > 0 && (
        <div className="flex flex-col gap-3 border-b border-solid py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.includedServices')}
          </h3>
          {visibleIncludedServices.map((service) => {
            return (
              <SetupServiceListItem key={service.serviceId} service={service} />
            )
          })}
        </div>
      )}

      {hasVisibleTicketGroups ? (
        <div className="flex flex-col gap-3 border-b border-solid py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.ticketServices')}
          </h3>

          <SetupTicketGlobalConstraints
            hasReservationMaxError={
              showReservationErrors && Boolean(globalMaxError)
            }
            hasReservationMinError={
              showReservationErrors && Boolean(globalMinError)
            }
            hasSpotMaxError={showSpotErrors && Boolean(globalSpotMaxError)}
            hasSpotMinError={showSpotErrors && Boolean(globalSpotMinError)}
            reservationLimitQuantity={
              servicesData?.reservationLimitQuantity ?? 0
            }
            reservationMinimumQuantity={
              servicesData?.reservationMinimumQuantity ?? 0
            }
            spotLimitQuantity={servicesData?.spotLimitQuantity ?? 0}
            spotMinimumQuantity={servicesData?.spotMinimumQuantity ?? 0}
          />

          <SetupTicketGroups
            getOverrideValues={getOverideValues}
            groups={visibleTicketGroups}
            isMultiGroup={visibleTicketGroups.length > 1}
            submitted={submitted}
          />
        </div>
      ) : null}

      {visibleExtraServices.length > 0 && (
        <div className="flex flex-col gap-3 py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.extraServices')}
          </h3>
          {visibleExtraServices.map((service) => (
            <SetupServiceListItem key={service.serviceId} service={service} />
          ))}
        </div>
      )}
    </div>
  )
}
